/** Read-only recovery from the existing receipt after the editable study has been removed. */
import { useQuery } from '@tanstack/react-query'
import { shopifyQueryPolicies } from '../../queries/shopifyQueryPolicies'
import type { StudyAuthoringDeletionReceipt } from '@testfeed/shopify-contracts'
import { shopifyComponentCopy } from '@testfeed/product-language'
import type { ShopifyAppBridge } from '../../shopifyApiFetch'
import { useShopifyAppContext } from '../../ShopifyAppContext'
import { readStudyAuthoringDeletionStatus } from '../../hooks/study-builder/studyAuthoringClient'
import { DirectStudyDeleteControl } from './DirectStudyDeleteControl'
import { ShopifyBackActionButton } from '../ShopifyPageActionButtons'
import { PageNotFound } from '../PageNotFound'
export function DirectStudyDeletion({
  shopify,
  receipt,
  onBack,
  lifecycle,
}: {
  shopify: ShopifyAppBridge
  receipt: StudyAuthoringDeletionReceipt
  onBack: () => void
  lifecycle?: Parameters<typeof DirectStudyDeleteControl>[0]['lifecycle']
}) {
  const scope = useShopifyAppContext()
  const query = useQuery({
    ...shopifyQueryPolicies.deletionStatus,
    queryKey: [
      'shopify-study-authoring',
      scope.shopDomain,
      scope.orgId,
      scope.connectionId,
      'deletion',
      receipt.operationId,
    ],
    initialData: receipt,
    retry: false,
    queryFn: ({ signal }) =>
      readStudyAuthoringDeletionStatus(shopify, { studyId: receipt.studyId, operationId: receipt.operationId }, signal),
    refetchInterval: (q) => (q.state.data?.status === 'accepted' || q.state.data?.status === 'cleaning' ? 5000 : false),
  })
  const current = query.data
  const copy = shopifyComponentCopy.studyBuilderPage
  let heading = copy.deletingTitle
  let message = copy.deletingBody
  if (current.status === 'failed') {
    heading = copy.deletionFailedTitle
    message = copy.deletionFailedBody
  }
  let content = (
    <s-section>
      <s-paragraph>{message}</s-paragraph>
    </s-section>
  )
  if (current.status === 'completed') {
    heading = copy.untitledHeading
    content = (
      <PageNotFound
        heading={copy.deletedTitle}
        body={copy.deletedBody}
        setupId={receipt.studyId}
        onBack={onBack}
        action={{ content: shopifyComponentCopy.pageNotFound.backToTests, onAction: onBack }}
        secondaryAction={null}
      />
    )
  }
  return (
    <s-page heading={heading} inlineSize="base">
      <ShopifyBackActionButton action={{ content: copy.testsBack, onAction: onBack }} />
      {!query.isError && (
        <DirectStudyDeleteControl
          shopify={shopify}
          studyId={receipt.studyId}
          receipt={current}
          title={copy.deletionTitle}
          onDeleted={onBack}
          lifecycle={lifecycle}
        />
      )}
      {content}
      {query.isError && (
        <s-banner tone="critical">
          {copy.deletionStatusFailed}
          <s-button onClick={() => void query.refetch()}>{shopifyComponentCopy.studySetupCard.tryAgain026}</s-button>
        </s-banner>
      )}
    </s-page>
  )
}
